import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import QRCode from "qrcode"
import { FC, useEffect, useRef } from "react"
import { ResponsiveDialog } from "../ResponsiveDialog/ResponsiveDialog"

interface QRCodeGeneratorProps {
  open: boolean
  onClose: () => void
  barcode: string
  title: string
}

export const QRCodeGenerator: FC<QRCodeGeneratorProps> = ({
  open,
  onClose,
  barcode,
  title,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const generateQRCodeWithText = async (
    textToEncode: string,
    titleText: string,
    barcodeText: string
  ) => {
    if (!canvasRef.current || !textToEncode.trim()) {
      console.log("Canvas nicht verfügbar oder Text leer:", {
        canvasRef: !!canvasRef.current,
        text: textToEncode,
      })
      return
    }

    try {
      // QR-Code generieren
      const qrDataURL = await QRCode.toDataURL(textToEncode, {
        width: 256,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      })

      // Canvas für das finale Bild erstellen
      const canvas = canvasRef.current
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      // Canvas-Größe setzen (QR-Code + Platz für Text oben und unten)
      const qrSize = 256
      const padding = 0
      const titleHeight = 20
      const barcodeHeight = 20
      const totalHeight = qrSize + titleHeight + barcodeHeight + padding * 2
      const totalWidth = qrSize + padding * 2

      canvas.width = totalWidth
      canvas.height = totalHeight

      // Hintergrund weiß füllen
      ctx.fillStyle = "#FFFFFF"
      ctx.fillRect(0, 0, totalWidth, totalHeight)

      // QR-Code laden und zeichnen
      const qrImage = new Image()
      qrImage.onload = () => {
        // QR-Code in der Mitte zeichnen
        ctx.drawImage(qrImage, padding, titleHeight + padding, qrSize, qrSize)

        // Titel oben zeichnen
        ctx.fillStyle = "#000000"
        ctx.font = "bold 16px Arial"
        ctx.textAlign = "center"
        ctx.fillText(titleText, totalWidth / 2, titleHeight - 2)

        // Barcode unten zeichnen
        ctx.font = "14px Arial"
        ctx.fillText(barcodeText, totalWidth / 2, totalHeight - 5)
      }
      qrImage.src = qrDataURL
    } catch (error) {
      console.error("Fehler beim Generieren des QR-Codes:", error)
    }
  }

  useEffect(() => {
    if (open && barcode.trim()) {
      const timer = setTimeout(() => {
        generateQRCodeWithText(barcode, title, barcode)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [open, barcode, title])

  const handleDownload = () => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const link = document.createElement("a")
    link.download = `qrcode-${title}-${barcode}-${Date.now()}.png`
    link.href = canvas.toDataURL()
    link.click()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <ResponsiveDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        QR-Code für {title} ({barcode})
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          {barcode.trim() && (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Typography>QR-Code für: {barcode}</Typography>
            </Box>
          )}

          {barcode.trim() && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                backgroundColor: "#fafafa",
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
            </Box>
          )}

          {!barcode.trim() && (
            <Typography variant="body2" color="text.secondary">
              Kein Text für QR-Code angegeben.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
        {barcode.trim() && (
          <Button onClick={handleDownload} variant="contained" color="primary">
            QR-Code herunterladen
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  )
}
